/**
 * Admin Deliveries Page
 * View all deliveries from all drivers in table format
 */

import { useState, useMemo } from 'react'
import { Package, Download, MapPin, Image as ImageIcon, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { Card, Input, Button } from '../ui'
import { useAppStore } from '../../stores'
import { formatDate, formatDateDDMMYYYY, formatTime } from '../../utils/helpers'
import { useDebounce } from '../../hooks'

export function AdminDeliveries() {
  const { deliveries } = useAppStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [expandedPhoto, setExpandedPhoto] = useState<string | null>(null)
  const itemsPerPage = 10
  
  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  
  // Filter deliveries with useMemo for performance
  const filteredDeliveries = useMemo(() => {
    return deliveries.filter(d => {
      const matchesSearch = d.driverId.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        d.note?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      const matchesStatus = statusFilter === 'all' || d.status === statusFilter
      return matchesSearch && matchesStatus
    }).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
  }, [deliveries, debouncedSearchTerm, statusFilter])
  
  // Pagination
  const totalPages = Math.ceil(filteredDeliveries.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedDeliveries = useMemo(() => 
    filteredDeliveries.slice(startIndex, endIndex),
    [filteredDeliveries, startIndex, endIndex]
  )
  
  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }
  
  const handleStatusChange = (value: string) => {
    setStatusFilter(value)
    setCurrentPage(1)
  }
  
  const getMapUrl = (lat: number, lng: number) => {
    return `https://www.google.com/maps?q=${lat},${lng}`
  }
  
  // Export to CSV
  const handleExport = () => {
    const headers = ['No', 'Driver ID', 'Date', 'Time', 'Store Name', 'Location', 'Note']
    const rows = filteredDeliveries.map((d, i) => [
      i + 1,
      d.driverId,
      formatDate(new Date(d.timestamp)),
      formatTime(new Date(d.timestamp)),
      d.note || '-',
      `${d.location.latitude}, ${d.location.longitude}`,
      d.note || '-'
    ])
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `deliveries-${formatDate(new Date())}.csv`
    a.click()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium text-gray-900">All Deliveries</h1>
          <p className="text-sm text-gray-500">{filteredDeliveries.length} total</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>
      
      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by driver or note..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="synced">Synced</option>
              <option value="delivered">Delivered</option>
              <option value="error">Error</option>
            </select>
          </div>
        </div>
      </Card>
      
      {/* Table */}
      {filteredDeliveries.length === 0 ? (
        <Card className="text-center py-12">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No deliveries found</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider w-16">No.</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider w-32">Name</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider w-28">Date</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider w-24">Time</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider">StoreName</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider w-24">Location</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-600 uppercase tracking-wider w-20">Photo</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedDeliveries.map((delivery, index) => (
                  <tr key={delivery.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900 text-center whitespace-nowrap">{startIndex + index + 1}</td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900 text-center whitespace-nowrap overflow-hidden text-ellipsis">{delivery.driverId}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-center whitespace-nowrap">{formatDateDDMMYYYY(new Date(delivery.timestamp))}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-center whitespace-nowrap">{formatTime(new Date(delivery.timestamp))}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-center whitespace-nowrap overflow-hidden text-ellipsis">
                      {delivery.note ? (
                        <span>{delivery.note}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-center whitespace-nowrap">
                      <a
                        href={getMapUrl(delivery.location.latitude, delivery.location.longitude)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                      >
                        <MapPin className="w-3 h-3" />
                        View
                      </a>
                    </td>
                    <td className="py-3 px-4 text-center whitespace-nowrap">
                      {delivery.photo ? (
                        <button
                          onClick={() => setExpandedPhoto(delivery.photo!)}
                          className="inline-flex items-center justify-center w-12 h-12 rounded-lg overflow-hidden hover:opacity-80 transition-opacity mx-auto cursor-pointer"
                        >
                          <img
                            src={delivery.photo}
                            alt="Delivery"
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ) : (
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gray-100 mx-auto">
                          <ImageIcon className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredDeliveries.length)} of {filteredDeliveries.length} deliveries
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Photo Modal */}
      {expandedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={() => setExpandedPhoto(null)}
        >
          <button
            onClick={() => setExpandedPhoto(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={expandedPhoto}
            alt="Expanded delivery photo"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  )
}
